import * as React from 'react'
import axios from 'axios'
import { useLocale, useTranslation } from 'hooks'
import { withTranslations, encodeUriWith, stripeHelpers } from 'helpers'
import InputField from './atoms/InputField/InputField'
import Card from './atoms/Card'

/**
 * Renders a new payment form component.
 * Only used on payment (including deposit) paths
 *
 * @param {NewPaymentFormProps} NewPaymentFormProps - The component props.
 * @returns {JSX.Element} - The rendered component.
 */
const NewPaymentForm = ({
  containingFormSelector,
  publishableKey,
  allowBalancePayment,
  buttonText,
  resourceName,
  trustSignalUrl,
  userEmail,
  paymentIntentSecret,
  userPhoneNumber = '',
  validateCartItems = true,
  includeMarketingConsent = false,
  marketingConsent = false,
  includeSmsMarketingConsent = false,
  smsMarketingConsent = false,
  billingAddress = undefined,
  shippingAddress = undefined,
  cartTimestamp = undefined,
  defaultPaymentMethod = 'balance',
  balance = undefined,
  discountedBalanceCost = undefined,
  showHr = true
}) => {
  const { t } = useTranslation()
  const { localiseUrl, locale } = useLocale()
  const stripeRef = React.useRef(
    window.Stripe?.(publishableKey, {
      betas: ['payment_intent_beta_3'],
      locale
    })
  )
  /** Stripe card element */
  const cardEl = React.useRef(
    stripeHelpers.createStripeCardElement(stripeRef.current)
  )
  const [paymentMethod, setPaymentMethod] = React.useState(
    allowBalancePayment ? defaultPaymentMethod : 'stripe'
  )
  const [status, setStatus] = React.useState(
    /** @type {'ready'|'loading'} */ ('ready')
  )
  const [cardHolderName, setCardHolderName] = React.useState('')
  const [error, setError] = React.useState(null)
  const [paymentIntentId, setPaymentIntentId] = React.useState('')
  const [shouldSubmit, setShouldSubmit] = React.useState(false)

  /**
   * On first render will mount the stripe card element on the page
   * Will also mount the stripe card element if the user switches from
   * using their balance as the payment method, to stripe as the payment method
   */
  React.useEffect(() => {
    if (paymentMethod === 'stripe') {
      cardEl.current.mount('#card-element')
    }
  }, [paymentMethod])

  /**
   * Helper function which should return the <form> element
   *
   * This form element is determined by the implementor of this component
   */
  const containingForm = () => {
    return document.querySelector(containingFormSelector)
  }

  const showError = (message) => {
    setError(message)
    setStatus('ready')
  }

  const updatePaymentMethod = (e) => {
    if (status !== 'ready') return
    setPaymentMethod(e.target.value)
  }

  const updateCardHolderName = (e) => {
    setCardHolderName(e.target.value)
  }

  const validateAndSubmit = (processPayment) => {
    // Skip cart validation and just process payment
    if (!validateCartItems) {
      processPayment()
      return
    }

    // API request ensures items are still available before processing payment
    axios
      .get(localiseUrl('/api/orders/validate_items', { localeAsParam: true }), {
        params: {
          cart_timestamp: cartTimestamp
        }
      })
      // Only a failed validation means the cart changed. An error while
      // taking the payment used to land here too and showed as "items
      // unavailable".
      .then(
        () => processPayment(),
        (err) => {
          const errorFromResponse = err?.response?.data?.error || '{}'
          const cartChanged = Object.keys(
            JSON.parse(errorFromResponse)
          ).includes('cart')
          const newLocation = `/cart?${
            cartChanged ? 'cart_invalid' : 'items_invalid'
          }=true`

          window.location.href = encodeUriWith(
            'encodeURI',
            localiseUrl(newLocation)
          )
        }
      )
      .catch(showGenericError)
  }

  const showGenericError = () =>
    showError(
      t('react_components.payment.generic_err', {
        // Until the key is published to the translations bucket.
        defaultValue: 'Something went wrong. Please try again.'
      })
    )

  /**
   * @returns {import('@stripe/stripe-js').PaymentMethodCreateParams.BillingDetails['address'] }
   */
  const getBillingAddress = () => {
    if (!billingAddress) return {}
    return {
      line1: billingAddress.line_1,
      line2: billingAddress.line_2,
      city: billingAddress.city,
      state: billingAddress.state,
      country: billingAddress.country,
      postal_code: billingAddress.postcode
    }
  }

  /**
   * @returns {import('@stripe/stripe-js').PaymentIntentConfirmParams.Shipping | null | undefined }
   */
  const getShippingData = () => {
    if (!shippingAddress) return undefined
    return {
      address: {
        line1: shippingAddress.line_1,
        line2: shippingAddress.line_2,
        city: shippingAddress.city,
        state: shippingAddress.state,
        country: shippingAddress.country,
        postal_code: shippingAddress.postcode
      },
      name: shippingAddress.full_name,
      phone: userPhoneNumber
    }
  }

  /**
   * Used to process a payment using Stripe's payment processing API.
   * It retrieves the payment details from the form, then confirms the payment with the Stripe API,
   * If the payment is successful, we attach the resulting payment intent ID to the form on the page
   * then submit the form to our own server (which now contains the payment intent ID and all other payment details)
   */
  const processStripePayment = async () => {
    // Stripe.js didn't load (blocked or offline): say so rather than leave
    // the button spinning.
    if (!stripeRef.current) {
      showGenericError()
      return
    }

    await stripeRef.current
      .confirmCardPayment(paymentIntentSecret, {
        payment_method: {
          card: cardEl.current,
          billing_details: {
            address: getBillingAddress(),
            email: userEmail,
            name: cardHolderName,
            phone: userPhoneNumber
          }
        },
        shipping: getShippingData()
      })
      .then((result) => {
        if (result.error) {
          showError(result.error.message)
          return
        }

        setPaymentIntentId(result.paymentIntent.id)
        setShouldSubmit(true)
      })
  }

  /**
   * Handles submit logic when the user clicks the confirmation button
   * If balance is selected: will take payment by directly sending a request to our servers
   *
   * If stripe is selected: will call `processStripePayment` to first take the payment via Stripe
   */
  const handleSubmit = (e) => {
    e.preventDefault()

    if (status !== 'ready') return

    if (paymentMethod === 'balance') {
      validateAndSubmit(() => {
        setStatus('loading')
        containingForm().submit()
      })
      return
    }

    if (!cardHolderName) {
      showError(t('react_components.payment.card_holder_err'))
      return
    }

    setError(null)
    setStatus('loading')

    validateAndSubmit(processStripePayment)
  }

  React.useEffect(() => {
    if (shouldSubmit) containingForm().submit()
  }, [containingForm, shouldSubmit])

  let balanceString = null

  if (allowBalancePayment && balance) {
    balanceString = discountedBalanceCost
      ? t('react_components.payment.account_balance_discounted', {
          balance: balance,
          discounted_cost: discountedBalanceCost
        })
      : t('react_components.payment.account_balance', {
          balance: balance
        })
  }

  return (
    <div>
      <input
        type="hidden"
        name={`${resourceName}[payment_intent_id]`}
        value={paymentIntentId}
      />
      <Card title={t('shared.payment_details')}>
        <div className={showHr ? '' : 'fieldset--mb-lg'}>
          {allowBalancePayment && (
            <div className="field no-mb field--radio">
              <input
                type="radio"
                checked={paymentMethod === 'balance'}
                value="balance"
                name={`${resourceName}[payment_method]`}
                id={`${resourceName}_payment_method_balance`}
                onChange={updatePaymentMethod}
              />
              <label htmlFor={`${resourceName}_payment_method_balance`}>
                <span className="caption">{balanceString} </span>
              </label>
            </div>
          )}
          <div className="field field--radio">
            <input
              type="radio"
              checked={paymentMethod === 'stripe'}
              value="stripe"
              name={`${resourceName}[payment_method]`}
              id={`${resourceName}_payment_method_stripe`}
              onChange={updatePaymentMethod}
            />
            <label htmlFor={`${resourceName}_payment_method_stripe`}>
              <span className="caption">
                {t('react_components.payment.credit_or_debit')}
              </span>
            </label>
          </div>
        </div>

        {paymentMethod === 'stripe' && (
          <>
            {showHr && <hr />}

            <div className="field">
              <InputField
                labelText={t('react_components.payment.cardholder_name')}
                type="text"
                id="cardholder_name"
                name="cardholder_name"
                autoComplete="cc-name"
                value={cardHolderName}
                onBlur={updateCardHolderName}
                onChange={updateCardHolderName}
              />
            </div>

            <div className="field">
              <label htmlFor="card-element">
                {t('react_components.payment.card_info')}
              </label>
              <div id="card-element" />
            </div>
            {error && <div className="form-group__error-message">{error}</div>}
            <p className="justify">
              <span className="caption">
                {t('react_components.payment.payment_secure')}
              </span>
              <a
                href="https://stripe.com/gb"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={trustSignalUrl} alt="Stripe" width="100" />
              </a>
            </p>
          </>
        )}
      </Card>
      {includeMarketingConsent && (
        <div className="field field--checkbox--marketing">
          <input
            type="checkbox"
            name={`${resourceName}[checkout_marketing_consent]`}
            id="checkout_marketing_consent"
            defaultChecked={marketingConsent}
          />
          <label
            className="no-transform marketing-consent-checkbox"
            htmlFor="checkout_marketing_consent"
          >
            {t('shared.marketing_consent')}
          </label>
        </div>
      )}
      {includeSmsMarketingConsent && (
        <div className="field field--checkbox--marketing">
          <input
            type="checkbox"
            name={`${resourceName}[checkout_sms_marketing_consent]`}
            id="checkout_sms_marketing_consent"
            defaultChecked={smsMarketingConsent}
          />
          <label
            className="no-transform marketing-consent-checkbox"
            htmlFor="checkout_sms_marketing_consent"
          >
            {t('shared.sms_marketing_consent')}
          </label>
        </div>
      )}
      <div className="card__actions button-group button-group--end">
        <button
          type="button"
          className={`button-group__item btn btn--primary ${
            status !== 'ready' ? 'btn--loading' : ''
          }`}
          onClick={handleSubmit}
        >
          <span className="btn--label">{buttonText}</span>
        </button>
      </div>
    </div>
  )
}

/**
 * @typedef {{
 * userEmail: string,
 * userPhoneNumber?: string,
 * billingAddress?: import('types/address').Address,
 * shippingAddress?: import('types/address').Address,
 * cartTimestamp?: string,
 * resourceName: string,
 * containingFormSelector: string,
 * publishableKey: string,
 * paymentIntentSecret: string,
 * allowBalancePayment: boolean,
 * defaultPaymentMethod?: string,
 * balance?: number,
 * buttonText: string,
 * cancelHref?: string,
 * trustSignalUrl: string,
 * validateCartItems?: boolean,
 * includeMarketingConsent?: boolean,
 * marketingConsent?: boolean,
 * includeSmsMarketingConsent?: boolean,
 * smsMarketingConsent?: boolean,
 * showHr?: boolean
 * }} NewPaymentFormProps
 */

export default withTranslations(NewPaymentForm)
